import { useEffect, useRef, useState } from 'react';
import type { SessionUser } from '@chemoflow/contracts';
import { Icon } from './Icon.js';
import { fmt, todayLabel } from '../lib/format.js';
import { ROLE_PROFILE, type ViewName } from '../lib/roles.js';

export interface HeaderProps {
  user: SessionUser;
  now: number;
  updatedAt: number;
  onNavigate(view: ViewName | 'home'): void;
  onCalls(): void;
  onLogout(): void;
}

/** 本番では各部署が自分の画面だけを見るため、部署を切り替えるタブは置かない。 */
export function Header({ user, now, updatedAt, onNavigate, onCalls, onLogout }: HeaderProps) {
  const [open, setOpen] = useState(false);
  const meRef = useRef<HTMLDivElement>(null);
  const profile = ROLE_PROFILE[user.role];

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!meRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);

  const go = (view: ViewName | 'home') => {
    setOpen(false);
    onNavigate(view);
  };

  return (
    <header className="top" id="top">
      <button className="brand" type="button" onClick={() => go('home')}>
        <b>ケモフロー</b>
        <span className="dept">{profile.dept}</span>
        <small>{todayLabel(now)}</small>
      </button>
      <div className="me" ref={meRef}>
        <span className="sync">
          <i />
          <span>{fmt(updatedAt)}</span> 更新
        </span>
        <button
          className="me-btn"
          type="button"
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          <span>
            {user.displayName}
            <span className="me-role">（{profile.label}）</span>
          </span>
          <Icon name="person" />
        </button>
        <div className="menu" role="menu" hidden={!open}>
          <button type="button" onClick={() => go('home')}>
            <Icon name={profile.icon} />
            {profile.dept}の画面
          </button>
          <button type="button" onClick={() => go('board')}>
            <Icon name="dashboard" />
            全体ボード
          </button>
          <hr />
          <button type="button" onClick={() => go('stats')}>
            <Icon name="bar_chart" />
            集計・出力
          </button>
          <button type="button" onClick={() => go('monitor')}>
            <Icon name="tv" />
            大型モニタ表示
          </button>
          <button type="button" onClick={() => go('admin')}>
            <Icon name="settings" />
            名簿・設定・操作ログ
          </button>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onCalls();
            }}
          >
            <Icon name="call" />
            内線番号
          </button>
          <hr />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onLogout();
            }}
          >
            <Icon name="logout" />
            ログアウト
          </button>
        </div>
      </div>
    </header>
  );
}
